import React, { useState } from 'react'
import { Container, Row, Col, Form } from 'react-bootstrap'
import { Helmet } from 'react-helmet-async'
import {
  FaCheckCircle,
  FaTrophy,
  FaHandshake,
  FaTruck,
  FaChartLine,
  FaWhatsapp,
} from 'react-icons/fa'
import ImagePlaceholder from '../components/ImagePlaceholder'

const BENEFITS = [
  { Icon: FaTrophy,    title: 'Marca referência nacional',   text: 'Associe seu negócio a uma marca pioneira e consolidada no mercado de nutrição esportiva.' },
  { Icon: FaHandshake, title: 'Suporte completo',            text: 'Oferecemos todo o suporte de treinamento, material de divulgação e atendimento ao revendedor.' },
  { Icon: FaTruck,     title: 'Entrega na sua porta',        text: 'Nossa frota própria refrigerada garante entregas pontuais com os produtos em perfeito estado.' },
  { Icon: FaChartLine, title: 'Alto potencial de retorno',   text: 'Mercado de alimentação saudável em expansão. Aumente sua renda com produtos de alta demanda.' },
]

const STATES = ['SP', 'MG', 'RJ', 'PR', 'SC', 'RS', 'GO', 'MT', 'MS', 'DF', 'BA', 'CE', 'PE', 'AM', 'PA', 'Outro']

export default function SejaRevendedor() {
  const [form, setForm] = useState({
    nome: '', email: '', telefone: '', cidade: '', estado: '', negocio: '', mensagem: '',
  })
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setLoading(true)
    // Simulate form submission – integrate with backend or form service as needed
    setTimeout(() => {
      setLoading(false)
      setSubmitted(true)
    }, 800)
  }

  return (
    <>
      <Helmet>
        <title>Seja um Revendedor – Clean Foods Brasil</title>
        <meta name="description" content="Seja um revendedor autorizado Clean Foods Brasil. Faça parte da maior rede de marmitas congeladas saudáveis do interior de São Paulo." />
      </Helmet>

      {/* ===== PAGE HERO ===== */}
      <div style={{ paddingTop: 72 }}>
        <section className="page-hero">
          <Container>
            <Row>
              <Col lg={8}>
                <p className="text-cf-yellow fw-bold text-uppercase small mb-2" style={{ letterSpacing: 2 }}>
                  Oportunidade de negócio
                </p>
                <h1 className="section-title mb-0">
                  Seja um <span className="highlight">Revendedor</span>
                </h1>
                <hr className="section-divider" />
                <p style={{ maxWidth: 580, lineHeight: 1.8 }}>
                  Faça parte da maior rede de marmitas congeladas saudáveis do interior de São Paulo.
                  Una-se à Clean Foods Brasil e leve nutrição de alto rendimento para a sua região.
                </p>
              </Col>
            </Row>
          </Container>
        </section>
      </div>

      {/* ===== POR QUE SER REVENDEDOR ===== */}
      <section className="section-padding bg-cf-black">
        <Container>
          <div className="text-center mb-5">
            <p className="text-cf-yellow fw-bold text-uppercase small mb-2" style={{ letterSpacing: 2 }}>
              Vantagens
            </p>
            <h2 className="section-title">
              Por que ser um <span className="highlight">revendedor?</span>
            </h2>
            <hr className="section-divider centered mx-auto" />
          </div>
          <Row className="gy-4">
            {BENEFITS.map(({ Icon, title, text }) => (
              <Col key={title} md={6} lg={3}>
                <div className="cf-card p-4 text-center h-100">
                  <div className="cf-card-icon mx-auto mb-3">
                    <Icon />
                  </div>
                  <h5 className="fw-bold mb-2" style={{ fontSize: '1rem' }}>{title}</h5>
                  <p className="mb-0" style={{ fontSize: '0.88rem', lineHeight: 1.7 }}>{text}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ===== COMO FUNCIONA ===== */}
      <section className="section-padding bg-cf-dark">
        <Container>
          <Row className="align-items-center gy-5">
            <Col lg={5}>
              <p className="text-cf-yellow fw-bold text-uppercase small mb-2" style={{ letterSpacing: 2 }}>
                Processo simples
              </p>
              <h2 className="section-title mb-0">
                Como se <span className="highlight">tornar</span> revendedor
              </h2>
              <hr className="section-divider" />
              <div className="d-flex flex-column gap-4 mt-4">
                {[
                  { step: '01', title: 'Preencha o formulário',   text: 'Envie seus dados pelo formulário ao lado e nossa equipe entrará em contato.' },
                  { step: '02', title: 'Análise e aprovação',     text: 'Nossa equipe avalia o perfil e o potencial de atendimento da sua região.' },
                  { step: '03', title: 'Credenciamento',          text: 'Após aprovação, você recebe o kit de boas-vindas e acesso a toda a linha de produtos.' },
                  { step: '04', title: 'Comece a vender!',        text: 'Receba seus pedidos com a nossa frota refrigerada e comece a faturar.' },
                ].map(({ step, title, text }) => (
                  <div key={step} className="d-flex gap-3 align-items-start">
                    <div
                      className="flex-shrink-0 d-flex align-items-center justify-content-center fw-black"
                      style={{
                        width: 44, height: 44, borderRadius: '50%',
                        background: 'var(--cf-yellow)', color: 'var(--cf-black)',
                        fontSize: '0.95rem', fontWeight: 900,
                      }}
                    >
                      {step}
                    </div>
                    <div>
                      <h6 className="fw-bold mb-1">{title}</h6>
                      <p className="mb-0" style={{ fontSize: '0.88rem', lineHeight: 1.6 }}>{text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Col>

            {/* ===== FORMULÁRIO ===== */}
            <Col lg={7}>
              <div className="cf-card p-4 p-md-5">
                {submitted ? (
                  <div className="text-center py-4">
                    <FaCheckCircle size={56} className="text-cf-yellow mb-3" />
                    <h4 className="fw-bold mb-2">Solicitação enviada com sucesso!</h4>
                    <p style={{ color: 'var(--cf-muted)' }}>
                      Recebemos seu interesse. Nossa equipe entrará em contato em breve pelo e-mail ou
                      telefone informados.
                    </p>
                    <button
                      className="btn btn-cf-outline mt-2"
                      onClick={() => { setSubmitted(false); setForm({ nome: '', email: '', telefone: '', cidade: '', estado: '', negocio: '', mensagem: '' }) }}
                    >
                      Enviar nova solicitação
                    </button>
                  </div>
                ) : (
                  <>
                    <h4 className="fw-bold mb-1">
                      Quero ser <span className="text-cf-yellow">revendedor</span>
                    </h4>
                    <p className="mb-4" style={{ fontSize: '0.9rem' }}>
                      Preencha os campos abaixo e entraremos em contato o mais breve possível.
                    </p>
                    <Form onSubmit={handleSubmit} className="cf-form">
                      <Row className="gy-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Nome completo *</Form.Label>
                            <Form.Control
                              type="text"
                              name="nome"
                              value={form.nome}
                              onChange={handleChange}
                              placeholder="Seu nome completo"
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>E-mail *</Form.Label>
                            <Form.Control
                              type="email"
                              name="email"
                              value={form.email}
                              onChange={handleChange}
                              placeholder="seu@email.com.br"
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Telefone / WhatsApp *</Form.Label>
                            <Form.Control
                              type="tel"
                              name="telefone"
                              value={form.telefone}
                              onChange={handleChange}
                              placeholder="(00) 9 0000-0000"
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Cidade *</Form.Label>
                            <Form.Control
                              type="text"
                              name="cidade"
                              value={form.cidade}
                              onChange={handleChange}
                              placeholder="Sua cidade"
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Estado *</Form.Label>
                            <Form.Select
                              name="estado"
                              value={form.estado}
                              onChange={handleChange}
                              required
                            >
                              <option value="">Selecione</option>
                              {STATES.map((s) => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Tipo de negócio</Form.Label>
                            <Form.Select name="negocio" value={form.negocio} onChange={handleChange}>
                              <option value="">Selecione</option>
                              <option>Academia / Crossfit</option>
                              <option>Suplementos / Nutrição esportiva</option>
                              <option>Empório / Loja de produtos naturais</option>
                              <option>Restaurante / Lanchonete</option>
                              <option>Consultório / Clínica</option>
                              <option>Outros</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col xs={12}>
                          <Form.Group>
                            <Form.Label>Mensagem (opcional)</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              name="mensagem"
                              value={form.mensagem}
                              onChange={handleChange}
                              placeholder="Conte-nos um pouco sobre você e seu negócio..."
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={12}>
                          <button
                            type="submit"
                            className="btn btn-cf-yellow w-100 py-3"
                            disabled={loading}
                          >
                            {loading ? 'Enviando...' : 'Enviar solicitação'}
                          </button>
                        </Col>
                      </Row>
                    </Form>
                  </>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ===== DEPOIMENTO PLACEHOLDER ===== */}
      <section className="section-padding bg-cf-black">
        <Container>
          <div className="text-center mb-5">
            <p className="text-cf-yellow fw-bold text-uppercase small mb-2" style={{ letterSpacing: 2 }}>
              Quem já faz parte
            </p>
            <h2 className="section-title">
              O que dizem nossos <span className="highlight">revendedores</span>
            </h2>
            <hr className="section-divider centered mx-auto" />
          </div>
          <Row className="gy-4">
            {[1, 2, 3].map((i) => (
              <Col key={i} md={4}>
                <div className="cf-card p-4 h-100">
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <ImagePlaceholder height="56px" label="" />
                    <div>
                      <p className="fw-bold mb-0" style={{ fontSize: '0.95rem' }}>Revendedor {i}</p>
                      <p className="mb-0" style={{ fontSize: '0.82rem' }}>Cidade, SP</p>
                    </div>
                  </div>
                  <p style={{ fontSize: '0.9rem', lineHeight: 1.7, fontStyle: 'italic' }}>
                    "Depoimento do revendedor a ser adicionado posteriormente."
                  </p>
                  <div className="text-cf-yellow">★★★★★</div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ===== WHATSAPP CTA ===== */}
      <section className="cta-section">
        <Container>
          <Row className="align-items-center gy-4">
            <Col lg={8}>
              <h2 className="cta-title mb-2">Prefere falar por WhatsApp?</h2>
              <p className="cta-text mb-0">
                Nossa equipe comercial está disponível para esclarecer todas as suas dúvidas.
              </p>
            </Col>
            <Col lg={4} className="text-lg-end">
              <a
                href="https://wa.me/5514991718448"
                target="_blank"
                rel="noreferrer"
                className="btn btn-lg fw-bold text-uppercase px-5 d-inline-flex align-items-center gap-2"
                style={{ background: 'var(--cf-black)', color: 'var(--cf-yellow)', borderRadius: 4 }}
              >
                <FaWhatsapp size={20} />
                Falar no WhatsApp
              </a>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  )
}
